<template>
  <v-card
    class="mb-4 px-2 pb-2 multi-volume-buys-card"
    :style="{ 'z-index': volumeBuysQty - volumeBuyPosition }"
  >
    <table id="multi-volume-buys-table">
      <thead class="multi-volume-buys-table-header">
        <tr class="multi-volume-buys-title-row">
          <th class="header-cell actions-column">Actions</th>
          <th class="header-cell promotion-type-column">Volume Buy Type</th>
          <th class="header-cell promotion-item-column">
            <template v-if="hasSelectedPromoType">
              <template
                v-if="
                  volumeBuy.selectedPromotionType ===
                  PROMOTION_TYPE.BY_FAMILY_GROUP
                "
                >Family Group</template
              >
              <template
                v-if="
                  volumeBuy.selectedPromotionType === PROMOTION_TYPE.BY_PRODUCT
                "
                >Product</template
              >
            </template>
            <template v-else>Volume Buy Selection(s)</template>
          </th>
          <th class="header-cell promotion-name-column">
            Volume Buy Name
            <v-icon
              class="i-tooltip-color"
              small
              v-tooltip="'Change name if required'"
            >
              mdi-help-circle-outline
            </v-icon>
          </th>
          <th class="header-cell start-date-column">Start Date</th>
          <th class="header-cell end-date-column">End Date</th>
          <th class="header-cell target-type-column">Target Type</th>
          <th class="header-cell target-item-column">
            <template v-if="hasSelectedTargetType">
              <template v-if="volumeBuy.targetType === TARGET_TYPE.BANNER_GROUP"
                >Banner Groups(s)</template
              >
              <template
                v-if="volumeBuy.targetType === TARGET_TYPE.MULTI_STORE_OPERATOR"
                >Multi-Store Operator(s)</template
              >
              <template
                v-if="volumeBuy.targetType === TARGET_TYPE.RETAILER_STORE"
                >{{ isILR ? 'Customer(s)' : 'Retailer(s)' }}</template
              >
            </template>
            <template v-else>Target Selection(s)</template>
          </th>
          <th class="header-cell pricing-column">Pricing</th>
          <th class="header-cell validation-column">Ready</th>
        </tr>

        <tr class="multi-volume-buys-data-row">
          <td class="actions-column">
            <v-btn
              :disabled="!isRowValid"
              icon
              v-tooltip="{
                content: 'Add Volume Buy',
                visible: $vuetifyDisplay.mdAndDown ? false : undefined
              }"
              @click="$emit('add')"
            >
              <v-icon class="action-icon">mdi-plus-circle-outline</v-icon>
            </v-btn>

            <v-btn
              :disabled="isOnlyRow"
              icon
              v-tooltip="{
                content: 'Remove Volume Buy',
                visible: $vuetifyDisplay.mdAndDown ? false : undefined
              }"
              @click="$emit('remove')"
            >
              <v-icon class="action-icon">mdi-minus-circle-outline</v-icon>
            </v-btn>

            <v-btn
              :disabled="!isRowValid && !isDuplicate"
              icon
              v-tooltip="{
                content: isDuplicate
                  ? 'Duplicated Volume Buy'
                  : 'Duplicate Volume Buy',
                visible: $vuetifyDisplay.mdAndDown ? false : undefined
              }"
              @click="onDuplicateClick"
            >
              <v-icon class="copy-icon" :class="{ duplicate: isDuplicate }"
                >mdi-content-duplicate</v-icon
              >
            </v-btn>
            <v-btn
              icon
              v-tooltip="{
                content: 'Reset Volume Buy to initial values',
                visible: $vuetifyDisplay.mdAndDown ? false : undefined
              }"
              @click="resetVolumeBuy"
            >
              <v-icon class="action-icon">mdi-backup-restore</v-icon>
            </v-btn>
          </td>

          <td class="promotion-type-column">
            <v-select
              label="Select Type *"
              attach
              dense
              required
              outlined
              v-model="volumeBuy.selectedPromotionType"
              :background-color="backgroundColor"
              :error="!volumeBuy.selectedPromotionType || isDuplicate"
              :items="promotionTypes"
              :rules="[(v) => !!v || 'Volume Buy Type required']"
              @change="onFieldChange('promotionTypes', $event)"
            />
          </td>

          <td class="promotion-item-column">
            <template v-if="hasSelectedPromoType">
              <template
                v-if="
                  volumeBuy.selectedPromotionType ===
                  PROMOTION_TYPE.BY_FAMILY_GROUP
                "
              >
                <v-autocomplete
                  label="Select Family Group *"
                  attach
                  outlined
                  dense
                  v-model="volumeBuy.selectedFamilyGroup"
                  :background-color="backgroundColor"
                  :error="!volumeBuy.selectedFamilyGroup || isDuplicate"
                  :disabled="!volumeBuy.selectedPromotionType"
                  :items="familyGroups"
                  item-disabled="disabled"
                  :rules="[(v) => !!v || 'Family Group required']"
                  @change="onFieldChange('selectedFamilyGroup', $event)"
                >
                  <template v-slot:selection="{ item }">
                    {{ item.text }}
                  </template>
                </v-autocomplete>
                <div
                  v-if="showUnavailableProductAlert"
                  class="inline-price-warning"
                  v-tooltip="upcomingPriceChangeWarningText"
                >
                  <v-icon class="inline-price-warning__icon" small>
                    mdi-alert
                  </v-icon>
                  <span class="inline-price-warning__text">
                    {{ upcomingPriceChangeWarningText }}
                  </span>
                </div>
              </template>
              <template
                v-if="
                  volumeBuy.selectedPromotionType === PROMOTION_TYPE.BY_PRODUCT
                "
              >
                <v-autocomplete
                  label="Select Product *"
                  attach
                  outlined
                  dense
                  clearable
                  v-model="volumeBuy.selectedProduct"
                  :background-color="backgroundColor"
                  :error="!volumeBuy.selectedProduct || isDuplicate"
                  :disabled="isProductSelectionDisabled"
                  :loading="isProductSelectionLoading"
                  :no-data-text="
                    isProductSelectionLoading
                      ? 'Loading products...'
                      : 'No data available'
                  "
                  :items="visibleProducts"
                  item-title="text"
                  item-text="text"
                  item-value="value"
                  item-disabled="disabled"
                  :rules="[
                    (v) => !!v || 'Product required (Live products only)'
                  ]"
                  v-model:search-input="productSearchInput"
                  @focus="emitEnsureProducts"
                  @click:clear="clearSelectedProduct"
                  @change="handleProductChange"
                >
                  <template v-slot:selection>
                    {{ selectedProductOption.text }}
                  </template>
                </v-autocomplete>
                <div
                  v-if="showUnavailableProductAlert"
                  class="inline-price-warning"
                  v-tooltip="upcomingPriceChangeWarningText"
                >
                  <v-icon class="inline-price-warning__icon" small>
                    mdi-alert
                  </v-icon>
                  <span class="inline-price-warning__text">
                    {{ upcomingPriceChangeWarningText }}
                  </span>
                </div>
              </template>
            </template>
            <template v-else>
              <v-select
                attach
                hide-details
                outlined
                dense
                disabled
                label="Volume Buy Selection(s)"
                :background-color="backgroundColor"
              />
            </template>
          </td>

          <td class="promotion-name-column">
            <v-text-field
              counter="60"
              dense
              label="Volume Buy Name *"
              maxlength="60"
              outlined
              v-model="volumeBuy.promotionName"
              :background-color="backgroundColor"
              :error="isDuplicate"
              :disabled="
                !(volumeBuy.selectedProduct || volumeBuy.selectedFamilyGroup)
              "
              :rules="[
                (v) => !!v || 'Volume Buy Name is required',
                (v) =>
                  (!!v && v.length <= 60) ||
                  'Volume Buy Name cannot exceed 60 characters'
              ]"
            >
            </v-text-field>
          </td>

          <td>
            <calendar-field
              label="Start Date *"
              :date="volumeBuy.startDate"
              :disabled="!volumeBuy.promotionName"
              :rules="[
                VALID_START_DATE(volumeBuy.startDate, volumeBuy.endDate)
              ]"
              :error="!volumeBuy.startDate || isDuplicate"
              :min="minAllowedStartDate"
              @dateChanged="onFieldChange('startDate', $event)"
            />
          </td>

          <td>
            <calendar-field
              label="End Date *"
              :date="volumeBuy.endDate"
              :disabled="!volumeBuy.startDate"
              :error="!volumeBuy.endDate || isDuplicate"
              :rules="[VALID_END_DATE(volumeBuy.startDate, volumeBuy.endDate)]"
              :min="minAllowedEndDate"
              @dateChanged="onFieldChange('endDate', $event)"
            />
          </td>

          <td>
            <v-select
              attach
              outlined
              required
              dense
              label="Select Type *"
              :background-color="backgroundColor"
              :error="!volumeBuy.targetType || isDuplicate"
              :disabled="!volumeBuy.endDate"
              :items="tenantTargetTypes"
              :rules="[(v) => !!v || 'Target Type required']"
              v-model="volumeBuy.targetType"
              @change="
                (onFieldChange('targetType', $event), resetTargetTypeForm())
              "
            />
          </td>

          <td class="target-item-column">
            <template v-if="hasSelectedTargetType">
              <template
                v-if="volumeBuy.targetType === TARGET_TYPE.BANNER_GROUP"
              >
                <v-autocomplete
                  chips
                  clearable
                  deletable-chips
                  small-chips
                  multiple
                  outlined
                  dense
                  label="Select Banner Group(s)"
                  item-text="displayName"
                  item-value="id"
                  :background-color="backgroundColor"
                  :disabled="!volumeBuy.targetType"
                  :error="!volumeBuy.targetBannerGroupIds || isDuplicate"
                  :items="bannerGroups"
                  :rules="[
                    (v) => (v && !!v.length) || 'Banner Group(s) required'
                  ]"
                  v-model="volumeBuy.targetBannerGroupIds"
                  @change="onFieldChange('targetBannerGroupIds', $event)"
                >
                  <template v-slot:selection="{ item, index }">
                    <template
                      v-if="volumeBuy.targetBannerGroupIds.length === 1"
                    >
                      <v-chip small>{{ item.displayName }}</v-chip>
                    </template>
                    <template
                      v-if="
                        volumeBuy.targetBannerGroupIds.length >= 2 &&
                        index === 1
                      "
                    >
                      <v-chip small
                        >{{ volumeBuy.targetBannerGroupIds.length }} BGs
                        selected</v-chip
                      >
                    </template>
                  </template>
                </v-autocomplete>
              </template>
              <template
                v-if="volumeBuy.targetType === TARGET_TYPE.MULTI_STORE_OPERATOR"
              >
                <v-autocomplete
                  chips
                  clearable
                  deletable-chips
                  multiple
                  outlined
                  dense
                  label="Select Multi-Store Operator(s)"
                  item-text="displayName"
                  item-value="id"
                  :background-color="backgroundColor"
                  :disabled="!volumeBuy.targetType"
                  :error="!volumeBuy.targetMultiStoreOperatorIds || isDuplicate"
                  :items="multiStoreOperators"
                  :rules="[
                    (v) =>
                      (v && !!v.length) || 'Multi-Store Operator(s) required'
                  ]"
                  v-model="volumeBuy.targetMultiStoreOperatorIds"
                  @change="onFieldChange('targetMultiStoreOperatorIds', $event)"
                >
                  <template v-slot:selection="{ item, index }">
                    <template
                      v-if="volumeBuy.targetMultiStoreOperatorIds.length === 1"
                    >
                      <v-chip small>{{ item.displayName }}</v-chip>
                    </template>
                    <template
                      v-if="
                        volumeBuy.targetMultiStoreOperatorIds.length >= 2 &&
                        index === 1
                      "
                    >
                      <v-chip small
                        >{{ volumeBuy.targetMultiStoreOperatorIds.length }} MSOs
                        selected</v-chip
                      >
                    </template>
                  </template>
                </v-autocomplete>
              </template>
              <template
                v-if="volumeBuy.targetType === TARGET_TYPE.RETAILER_STORE"
              >
                <v-autocomplete
                  chips
                  clearable
                  deletable-chips
                  multiple
                  outlined
                  dense
                  :label="`Select ${isILR ? 'Customer(s)' : 'Retailer(s)'}`"
                  item-text="displayName"
                  item-value="id"
                  :background-color="backgroundColor"
                  :disabled="!volumeBuy.targetType"
                  :error="!volumeBuy.targetStoreIds || isDuplicate"
                  :items="targetStores"
                  :rules="[
                    (v) =>
                      (v && !!v.length) ||
                      `${isILR ? 'Customer(s)' : 'Retailer(s)'} required`
                  ]"
                  v-model="volumeBuy.targetStoreIds"
                  @change="handleRetailerChange"
                >
                  <template v-slot:selection="{ item, index }">
                    <template v-if="volumeBuy.targetStoreIds.length === 1">
                      <v-chip small>{{ item.displayName }}</v-chip>
                    </template>
                    <template
                      v-if="volumeBuy.targetStoreIds.length >= 2 && index === 1"
                    >
                      <v-chip small
                        >{{ volumeBuy.targetStoreIds.length }}
                        {{ isILR ? 'Customers' : 'Retailers' }} selected</v-chip
                      >
                    </template>
                  </template>
                </v-autocomplete>
                <div class="retailer-button-wrap">
                  <retailer-list-action-button
                    :supplier-id="selectedSupplierId"
                    :selected-retailers="volumeBuy.targetStoreIds"
                    :disabled="!volumeBuy.targetType"
                    mode="useOnly"
                    @apply="applySavedRetailers"
                    :saved-list-applied="volumeBuy.savedListApplied"
                  />
                </div>
              </template>
            </template>
            <template v-else>
              <v-autocomplete
                :background-color="backgroundColor"
                hide-details
                label="Target Selection(s)"
                outlined
                dense
                disabled
              ></v-autocomplete>
            </template>
          </td>

          <td class="pricing-column">
            <v-btn
              class="pricing-button"
              :disabled="!canShowPricing"
              @click="onRowClick"
            >
              <template v-if="isExpand">
                <span>Hide</span>
                <v-icon small class="ml-1">mdi-arrow-up</v-icon>
              </template>
              <template v-else>
                <span>Show</span>
                <v-icon small class="ml-1">mdi-arrow-down</v-icon>
              </template>
            </v-btn>
          </td>

          <td class="validation-column">
            <div
              class="validation-background"
              :class="{ valid: isRowValid, invalid: !isRowValid }"
            >
              <v-icon v-if="isRowValid" color="green" class="pt-2"
                >mdi-check-circle-outline</v-icon
              >
              <v-icon v-else color="red" class="pt-2"
                >mdi-alert-circle-outline</v-icon
              >
            </div>
          </td>
        </tr>

        <template v-if="isExpandable && isExpand">
          <tr>
            <td colspan="10" class="px-2 py-1">
              <legacy-radio-group
                v-model="discountEntryMode"
                name="multi-volume-buy-discount-entry-mode"
                :options="discountEntryModeOptions"
                :default-col-props="{ cols: '12', sm: '3', class: 'py-0' }"
                class="mt-3 mb-2"
              />
              <update-price-to-all-zones
                class="volume-buy-update-all"
                mode="volumeBuy"
                :hide-unit-rrp="isServiceProductSelected || isUSCountry"
                :is-service-product="isServiceProductSelected"
                v-model:discount-entry-mode="discountEntryMode"
                @update-carton="onUpdateAmountOff"
                @update-discount-percentage="onUpdateDiscountPercentage"
                @update-rrp="onUpdateUnitRrp"
                @update-cartons="onUpdateCartons"
                @add-all-tiers="onAddAllTiers"
              />
            </td>
          </tr>
          <tr>
            <td colspan="10" class="pricing-subheader-cell">
              <table
                class="multi-volume-buys-data-table-headers"
                :class="{ 'service-mode': isServiceProductSelected }"
              >
                <thead>
                  <tr>
                    <th class="header-cell carton-price-column">
                      {{ isServiceProductSelected ? 'PRICE' : 'CARTON PRICE'
                      }}<br />(EX TAX)
                    </th>
                    <th class="header-cell number-of-cartons-column">
                      {{
                        isServiceProductSelected
                          ? 'NO. OF SERVICE'
                          : 'NUMBER OF CARTONS'
                      }}
                    </th>
                    <th class="header-cell amount-off-column">
                      {{
                        isServiceProductSelected
                          ? 'AMOUNT OFF'
                          : 'AMOUNT OFF CARTON'
                      }}<br />(EX TAX)
                    </th>
                    <th class="header-cell revised-unit-column">
                      REVISED UNIT<br />COST (EX TAX)
                    </th>
                    <th
                      v-if="!isUSCountry"
                      class="header-cell revised-unit-column"
                    >
                      REVISED UNIT<br />COST (INC TAX)
                    </th>
                    <th
                      v-if="!isServiceProductSelected && !isUSCountry"
                      class="header-cell volume-buy-unit-column"
                    >
                      ORIGINAL UNIT RRP<br />(INC TAX)
                    </th>
                    <th
                      v-if="!isServiceProductSelected && !isUSCountry"
                      class="header-cell volume-buy-unit-column"
                    >
                      VOLUME BUY UNIT RRP<br />(INC TAX)
                    </th>
                    <th
                      v-if="!isServiceProductSelected && !isUSCountry"
                      class="header-cell gpp-column"
                    >
                      GP%
                    </th>
                    <th class="header-cell actions-column">ACTIONS</th>
                  </tr>
                </thead>
              </table>
            </td>
          </tr>

          <tr>
            <td colspan="10" class="price-alerts">
              <v-alert
                border="left"
                outlined
                dense
                text
                type="info"
                elevation="1"
                class="my-2 i-alert"
              >
                For Zones without promotions, leave blank
              </v-alert>

              <v-alert
                v-if="!hasValidTier"
                border="left"
                dense
                outlined
                text
                type="error"
                elevation="1"
                class="my-2 i-alert"
              >
                <span>At least 1 zone with valid pricing is required</span>
              </v-alert>

              <v-alert
                v-if="hasUnsavedTiers && hasValidTier"
                border="left"
                dense
                outlined
                text
                type="error"
                elevation="1"
                class="my-2 i-alert"
              >
                <span
                  >This Promotion has Volume Tiers that have not been added
                  yet</span
                >
              </v-alert>
            </td>
          </tr>
        </template>
      </thead>

      <tbody>
        <tr v-if="isExpandable && isExpand">
          <td colspan="10">
            <div class="pricing-table-div">
              <volume-buy-tiers-table
                :active-promotion="false"
                :hasGst="volumeBuy.hasGst"
                :hasWet="volumeBuy.hasWet"
                :is-duplicate="isDuplicate"
                :isZoneNewTierValid="isServiceAwareZoneTierValid"
                :multiVolume="true"
                :package-quantity="volumeBuy.packageQuantity"
                :products="products"
                :selected-product="volumeBuy.selectedProduct"
                :variable-weight-target-in-kg="
                  volumeBuy.variableWeightTargetInKg
                "
                :zone-prices="volumeBuy.zonePrices"
                :is-service-product="isServiceProductSelected"
                :discount-entry-mode="discountEntryMode"
                @change="onFieldChange('zonePrices', $event)"
                ref="volumeBuyTierTable"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </v-card>
</template>

<script>
import { defineComponent, nextTick } from 'vue'

import CalendarField from '@/components/CalendarField'
import { mapActions, mapState, mapGetters } from 'vuex'
import {
  TARGET_TYPES,
  PROMOTION_TYPE,
  TARGET_TYPE
} from '@/modules/dataMaintenance/constants/Special.constants'
import {
  minAllowedPromotionEndDate,
  minAllowedPromotionStartDate
} from '@/modules/dataMaintenance/helper/PromotionPublishSchedule'
import VolumeBuyTiersTable from '@/modules/dataMaintenance/components/VolumeBuyTiersTable'
import VolumePromotionHelper, {
  DEFAULT_PROMOTION_TIER
} from '@/modules/dataMaintenance/helper/VolumePromotionHelper'
import RetailerListActionButton from '@/components/RetailerListActionButton.vue'
import UpdatePriceToAllZones from '@/components/UpdatePriceToAllZones.vue'
import LegacyRadioGroup from '@/components/LegacyRadioGroup.vue'
import { buildCountryDisplayRules } from '@/utils/countryDisplay'
import { buildVisibleProductOptions } from '@/modules/dataMaintenance/helper/ProductAutocompleteHelper'
import { UPCOMING_PRICE_CHANGE_WARNING_TEXT } from '@/modules/dataMaintenance/helper/PromotionProductAvailabilityHelper'
import {
  amountOffFromDiscountPercentage,
  DISCOUNT_ENTRY_MODE,
  setDiscountPercentageFromAmountOff
} from '@/modules/dataMaintenance/helper/DiscountEntryHelper'

const DATES_RULES = {
  VALID_START_DATE: (startDate, endDate) => {
    if (!startDate || !endDate) return true
    return endDate >= startDate || 'Start Date must be on or before End Date'
  },
  VALID_END_DATE: (startDate, endDate) => {
    if (!startDate && !endDate) return true
    if (!endDate) return 'End Date required'
    return endDate >= startDate || 'End Date must be on or after Start Date'
  }
}

export default defineComponent({
  emits: [
    'add',
    'remove',
    'duplicate',
    'change',
    'ensure-products',
    'search-products'
  ],

  components: {
    VolumeBuyTiersTable,
    CalendarField,
    LegacyRadioGroup,
    RetailerListActionButton,
    UpdatePriceToAllZones
  },

  props: {
    familyGroups: Array,
    isDuplicate: Boolean,
    products: Array,
    productsLoading: {
      type: Boolean,
      default: false
    },
    productsData: {
      type: Array,
      default: () => []
    },
    volumeBuy: { type: Object, required: true },
    volumeBuyPosition: { type: Number, required: true },
    volumeBuysQty: { type: Number, required: true },
    isOnlyRow: Boolean,
    initialValue: Object,
    isRowValid: Boolean
  },

  data: () => ({
    backgroundColor: '#0000000f',
    promotionTypes: [
      {
        title: 'By Family Group',
        key: PROMOTION_TYPE.BY_FAMILY_GROUP,
        text: 'By Family Group',
        value: PROMOTION_TYPE.BY_FAMILY_GROUP
      },
      {
        title: 'By Product',
        key: PROMOTION_TYPE.BY_PRODUCT,
        text: 'By Product',
        value: PROMOTION_TYPE.BY_PRODUCT
      }
    ],

    selectedFamilyGroup: null,
    selectedProduct: null,
    selectedPromotionType: null,
    startDate: null,
    endDate: null,
    targetType: null,
    targetBannerGroupIds: [],
    targetMultiStoreOperatorIds: [],
    targetStoreIds: [],
    savedListApplied: false,
    targetStores: [],
    targetBannerGroups: [],
    targetMultiStoreOperators: [],
    productSearchInput: '',
    discountEntryMode: DISCOUNT_ENTRY_MODE.AMOUNT,
    PROMOTION_TYPE,
    TARGET_TYPES,
    TARGET_TYPE,
    isExpand: false,
    ...DATES_RULES,
    upcomingPriceChangeWarningText: UPCOMING_PRICE_CHANGE_WARNING_TEXT
  }),

  computed: {
    ...mapState('bannerGroups', ['bannerGroups']),
    ...mapState('multiStoreOperators', ['multiStoreOperators']),
    ...mapState('stores', ['retailerStores']),
    ...mapGetters('tenant', ['isILR', 'tenantCountryRegion']),
    ...mapState('preferences', ['selectedSupplierId']),

    tenantTargetTypes() {
      return TARGET_TYPES.map((type) => {
        if (type.value === 'RETAILER_STORE') {
          return {
            ...type,
            text: this.isILR ? 'Customer(s)' : 'Retailer(s)'
          }
        }
        return type
      })
    },

    discountEntryModeOptions() {
      return [
        { label: 'Amount', value: DISCOUNT_ENTRY_MODE.AMOUNT },
        { label: '% Discount', value: DISCOUNT_ENTRY_MODE.PERCENTAGE }
      ]
    },

    // ***
    // Volume Buy related validators
    // ***

    canShowPricing() {
      return (
        (this.volumeBuy?.selectedFamilyGroup ||
          this.volumeBuy?.selectedProduct) &&
        (this.hasBannerGroupsSelected ||
          this.hasMultiStoreOperatorsSelected ||
          this.hasTargetStoresSelected ||
          this.TARGET_TYPE.NATIONAL)
      )
    },

    hasBannerGroupsSelected() {
      return this.volumeBuy?.targetBannerGroupIds?.length > 0
    },

    hasMultiStoreOperatorsSelected() {
      return this.volumeBuy?.targetMultiStoreOperatorIds?.length > 0
    },

    hasTargetStoresSelected() {
      return this.volumeBuy?.targetStoreIds?.length > 0
    },

    hasSelectedPromoType() {
      return [
        PROMOTION_TYPE.BY_FAMILY_GROUP,
        PROMOTION_TYPE.BY_PRODUCT
      ]?.includes(this.volumeBuy.selectedPromotionType)
    },

    hasSelectedTargetType() {
      return Object.values(this.TARGET_TYPE)?.includes(
        this.volumeBuy.targetType
      )
    },

    isProductSelectionLoading() {
      return this.productsLoading && !this.products.length
    },

    isProductSelectionDisabled() {
      return (
        !this.volumeBuy.selectedPromotionType || this.isProductSelectionLoading
      )
    },

    visibleProducts() {
      return buildVisibleProductOptions({
        options: this.products,
        searchTerm: this.productSearchInput,
        selectedValues: this.volumeBuy?.selectedProduct
          ? [this.volumeBuy.selectedProduct]
          : [],
        getSelectedValue: (selected) => selected
      })
    },

    selectedProductOption() {
      return this.products.find(
        (product) => product.value === this.volumeBuy.selectedProduct
      )
    },

    showUnavailableProductAlert() {
      return (
        this.products.some((product) => product.disabled) ||
        this.familyGroups.some((familyGroup) => familyGroup.disabled)
      )
    },

    isServiceProductSelected() {
      if (typeof this.volumeBuy?.isServiceProduct === 'boolean') {
        return this.volumeBuy.isServiceProduct
      }
      if (!this.volumeBuy?.selectedProduct) return false

      return (
        this.productsData.find(
          (p) => p.productCode === this.volumeBuy.selectedProduct
        )?.productType === 'SERVICE'
      )
    },

    unsavedTiersQuantity() {
      return VolumePromotionHelper.unsavedTiersQuantity(
        this.volumeBuy.zonePrices
      )
    },

    hasUnsavedTiers() {
      return VolumePromotionHelper.hasUnsavedTiers(this.volumeBuy.zonePrices)
    },

    hasValidTier() {
      return VolumePromotionHelper.hasValidTier(this.volumeBuy.zonePrices)
    },

    isInitialValue() {
      return (
        JSON.stringify(this.initialValue) === JSON.stringify(this.volumeBuy)
      )
    },

    isExpandable() {
      return this.volumeBuy?.zonePrices?.length > 0
    },

    minAllowedStartDate() {
      return minAllowedPromotionStartDate(this.tenantCountryRegion)
    },

    minAllowedEndDate() {
      return minAllowedPromotionEndDate(
        this.volumeBuy?.startDate,
        this.tenantCountryRegion
      )
    },

    displayRules() {
      return buildCountryDisplayRules(this.tenantCountryRegion)
    },

    isUSCountry() {
      return this.displayRules.isUSCountry
    }
  },

  watch: {
    discountEntryMode(newMode, oldMode) {
      if (oldMode && newMode !== oldMode) {
        this.clearDiscountPricing()
      }
    },

    productSearchInput(value) {
      if (
        this.volumeBuy.selectedPromotionType !== this.PROMOTION_TYPE.BY_PRODUCT
      ) {
        return
      }
      this.$emit('search-products', value || '')
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    emitEnsureProducts() {
      this.$emit('ensure-products')
    },

    clearSelectedProduct() {
      this.productSearchInput = ''
      this.$emit('change', {
        selectedProduct: null,
        selectedProductDisplayName: null,
        promotionName: null,
        zonePrices: []
      })
      this.$emit('search-products', '')
    },

    handleProductChange(value) {
      const productOption = this.products.find(
        (product) => product.value === value
      )
      if (productOption?.disabled) {
        this.clearSelectedProduct()
        return
      }

      this.onFieldChange('selectedProduct', value)
      this.productSearchInput = ''
    },

    onRowClick() {
      this.isExpand = !this.isExpand
    },

    onDuplicateClick() {
      if (this.isDuplicate) {
        return
      }
      this.$emit('duplicate')
    },

    resetTargetTypeForm() {
      this.targetBannerGroupIds = []
      this.targetMultiStoreOperatorIds = []
      this.targetStoreIds = []
    },

    onFieldChange(fieldName, value) {
      if (fieldName === 'promotionTypes') {
        this.$emit('change', {
          [fieldName]: value,
          selectedFamilyGroup: null,
          selectedProduct: null,
          zonePrices: []
        })
      } else if (fieldName === 'targetType') {
        this.$emit('change', {
          [fieldName]: value,
          targetBannerGroupIds: [],
          targetMultiStoreOperatorIds: [],
          targetStoreIds: []
        })
      } else {
        if (
          fieldName === 'selectedFamilyGroup' &&
          this.familyGroups.find((familyGroup) => familyGroup.value === value)
            ?.disabled
        ) {
          this.$emit('change', {
            selectedFamilyGroup: null,
            zonePrices: []
          })
          return
        }

        this.$emit('change', {
          [fieldName]: value
        })
      }
    },

    clearDiscountPricing() {
      const zonePrices = (this.volumeBuy.zonePrices || []).map((zone) => ({
        ...zone,
        tiers: [],
        newTier: { ...DEFAULT_PROMOTION_TIER }
      }))
      this.$emit('change', { zonePrices })
    },

    mapStoresList() {
      if (this.retailerStores?.length > 0) {
        // Map property names so that it works with V-select component
        this.targetStores = this.retailerStores.sort((a, b) =>
          a.displayName > b.displayName ? 1 : -1
        )

        if (this.regionalPriceMap?.length) {
          // Filter out stores not located in regions where the product is available
          this.targetStores = this.targetStores.filter((store) =>
            this.regionalPriceMap
              .map((p) => p.code)
              .includes(store.pricingRegionCode)
          )
        }
      } else {
        this.addNotification({
          color: 'secondary',
          text: `Available ${
            this.isILR ? 'Customers' : 'Retailers'
          } loading, please wait...`
        })
      }
    },

    mapBannerGroupsList() {
      if (this.bannerGroups?.length > 0) {
        // Map property names so that it works with V-select component
        this.targetBannerGroups = this.bannerGroups.sort((a, b) =>
          a.displayName > b.displayName ? 1 : -1
        )
      } else {
        this.addNotification({
          color: 'secondary',
          text: 'Available Banner Groups loading, please wait...'
        })
      }
    },

    mapMultiStoreOperatorsList() {
      if (this.multiStoreOperators?.length > 0) {
        // Map property names so that it works with V-select component
        this.targetMultiStoreOperators = this.multiStoreOperators.sort(
          (a, b) => (a.displayName > b.displayName ? 1 : -1)
        )
      } else {
        this.addNotification({
          color: 'secondary',
          text: 'Available MSOs loading, please wait...'
        })
      }
    },

    applySavedRetailers(ids) {
      this.volumeBuy.targetStoreIds = [...ids]
      this.volumeBuy.savedListApplied = true
    },

    handleRetailerChange(value) {
      this.volumeBuy.savedListApplied = false
      this.onFieldChange('targetStoreIds', value)
    },

    onUpdateAmountOff(value) {
      this.volumeBuy.zonePrices.forEach((zone) => {
        zone.newTier.amountOff = value
        setDiscountPercentageFromAmountOff(
          zone.newTier,
          zone.cartonPrice,
          value
        )
        this.$refs.volumeBuyTierTable.calculatePricingForTier(
          zone.newTier,
          zone,
          zone.cartonPrice
        )
      })

      nextTick(() => {
        if (
          this.$refs.volumeBuyTierTable &&
          typeof this.$refs.volumeBuyTierTable.validateAllTiers === 'function'
        ) {
          this.$refs.volumeBuyTierTable.validateAllTiers(
            this.$refs.volumeBuyTierTable.$refs
          )
        }
      })
    },

    onUpdateDiscountPercentage(value) {
      this.volumeBuy.zonePrices.forEach((zone) => {
        zone.newTier.discountPercentage = value
        zone.newTier.amountOff = amountOffFromDiscountPercentage(
          zone.cartonPrice,
          value
        )
        this.$refs.volumeBuyTierTable.calculatePricingForTier(
          zone.newTier,
          zone,
          zone.cartonPrice
        )
      })

      nextTick(() => {
        if (
          this.$refs.volumeBuyTierTable &&
          typeof this.$refs.volumeBuyTierTable.validateAllTiers === 'function'
        ) {
          this.$refs.volumeBuyTierTable.validateAllTiers(
            this.$refs.volumeBuyTierTable.$refs
          )
        }
      })
    },

    onUpdateUnitRrp(value) {
      if (this.isServiceProductSelected) return
      this.volumeBuy.zonePrices.forEach((zone) => {
        zone.newTier.unitRrp = value
        this.$refs.volumeBuyTierTable.calculatePricingForTier(
          zone.newTier,
          zone,
          zone.cartonPrice
        )
      })
    },

    onUpdateCartons(value) {
      this.volumeBuy.zonePrices.forEach((zone) => {
        zone.newTier.numberOfCartons = value
        this.$refs.volumeBuyTierTable.calculatePricingForTier(
          zone.newTier,
          zone,
          zone.cartonPrice
        )
      })
    },

    onAddAllTiers() {
      if (
        this.$refs.volumeBuyTierTable &&
        typeof this.$refs.volumeBuyTierTable.addAllEligibleTiers === 'function'
      ) {
        this.$refs.volumeBuyTierTable.addAllEligibleTiers()
      }
    },

    isServiceAwareZoneTierValid(zone) {
      if (!this.isServiceProductSelected) {
        return VolumePromotionHelper.isZoneNewTierValid(zone, this.isUSCountry)
      }

      const newTierCartons = Number(zone.newTier.numberOfCartons)
      const newAmountOff = Number(zone.newTier.amountOff)

      const sortedTiers = [...zone.tiers].sort(
        (a, b) => a.numberOfCartons - b.numberOfCartons
      )
      const lowerTier = sortedTiers
        .filter((tier) => tier.numberOfCartons < newTierCartons)
        .pop()
      const higherTier = sortedTiers.find(
        (tier) => tier.numberOfCartons > newTierCartons
      )

      const respectsLowerTier =
        !lowerTier || newAmountOff >= Number(lowerTier.amountOff)
      const respectsHigherTier =
        !higherTier || newAmountOff <= Number(higherTier.amountOff)

      return (
        [zone.newTier.numberOfCartons, zone.newTier.amountOff].every(
          (value) => Number(value) > 0
        ) &&
        zone.newTier.amountOff <= zone.cartonPrice &&
        !zone.tiers.find((tier) => tier.numberOfCartons === newTierCartons) &&
        respectsLowerTier &&
        respectsHigherTier
      )
    },

    resetVolumeBuy() {
      if (!this.initialValue) return

      const reset = JSON.parse(JSON.stringify(this.initialValue))

      // Manually update all known keys
      this.volumeBuy.selectedPromotionType = reset.selectedPromotionType
      this.volumeBuy.selectedProduct = reset.selectedProduct
      this.volumeBuy.selectedFamilyGroup = reset.selectedFamilyGroup
      this.volumeBuy.promotionName = reset.promotionName
      this.volumeBuy.startDate = reset.startDate
      this.volumeBuy.endDate = reset.endDate
      this.volumeBuy.targetType = reset.targetType
      this.volumeBuy.targetBannerGroupIds = [
        ...(reset.targetBannerGroupIds || [])
      ]

      this.volumeBuy.targetMultiStoreOperatorIds = [
        ...(reset.targetMultiStoreOperatorIds || [])
      ]

      this.volumeBuy.targetStoreIds = [...(reset.targetStoreIds || [])]
      this.volumeBuy.savedListApplied = reset.savedListApplied || false
      this.volumeBuy.zonePrices = JSON.parse(
        JSON.stringify(reset.zonePrices || [])
      )

      this.isExpand = false
    }
  },

  mounted() {
    this.mapStoresList()
    this.mapBannerGroupsList()
    this.mapMultiStoreOperatorsList()
  }
})
</script>

<style lang="scss">
#multi-volume-buys-table {
  .v-select.v-select--chips:not(
      .v-text-field--single-line
    ).v-text-field--enclosed.v-select--chips--small.v-input--dense
    .v-select__selections {
    min-height: 30px !important;
    max-height: 30px !important;
  }

  .v-select.v-select--chips.v-input--dense .v-select__selections {
    max-height: 30px !important;
  }
}
</style>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#multi-volume-buys-table {
  border-collapse: collapse;
  background-color: $bg-secondary-color;
  width: 100%;
  min-width: 1200px;
  table-layout: fixed;
  td:not(.price-alerts) {
    vertical-align: top;
    text-align: center;
    padding: 4px;
  }
  th {
    padding: 8px 8px 0 8px;
    white-space: nowrap;
  }
  .pricing-table-div {
    max-height: 530px;
    overflow-y: scroll;
    overflow-x: hidden;
    :deep(.v-data-table__wrapper > table) {
      width: calc(100% + 14px) !important;
      table-layout: fixed;
    }
  }
  .volume-buy-update-all {
    :deep(.i-update-all-zones-checkbox) {
      margin-top: 0 !important;
    }
  }
  .multi-volume-buys-table-header {
    background-color: $bg-secondary-color;
    .multi-volume-buys-data-row {
      &.body-expandable:hover {
        cursor: pointer;
      }
    }
    .multi-volume-buys-title-row {
      th {
        text-align: left;
        text-transform: uppercase;
        letter-spacing: 0.25px;
        padding-bottom: 4px;
      }
    }
    .multi-volume-buys-data-table-headers {
      width: 100%;
      table-layout: fixed;
      border-collapse: collapse;
      th {
        border-top: 2px solid $primary-color;
        padding: 16px 8px 4px 8px;
      }
      .carton-price-column,
      .revised-unit-column,
      .gpp-column,
      .actions-column {
        width: 11.11% !important;
        text-transform: uppercase;
        white-space: unset;
      }
      .number-of-cartons-column,
      .amount-off-column,
      .volume-buy-unit-column {
        width: 11.11% !important;
        text-transform: uppercase;
        white-space: unset;
      }
      &.service-mode {
        .carton-price-column,
        .revised-unit-column,
        .actions-column {
          width: 16.66% !important;
        }
        .number-of-cartons-column,
        .amount-off-column {
          width: 16.66% !important;
        }
      }
    }
    .pricing-subheader-cell {
      padding: 0 !important;
    }
    .actions-column {
      width: 9%;
      min-width: 150px;
      text-align: center !important;
      &:before {
        width: 9%;
        min-width: 150px;
      }
    }
    .action-icon {
      color: $primary-color;
    }
    .copy-icon {
      color: $primary-color;
      &.duplicate {
        color: $red-color !important;
      }
    }
    .expand-icon,
    .collapse-icon {
      &.expandable {
        color: $primary-color;
      }
    }
    .header-cell:before {
      content: ' ';
      display: block;
    }
    .pricing-column {
      width: 85px !important;
      text-align: center !important;
      &:before {
        width: 85px;
      }
      .pricing-button {
        background-color: $primary-color;
        color: white;
        text-transform: none;
        font-size: 14px;
        font-weight: bold;
        width: 75px;
        height: 39px;
      }
    }
    .pricing-table-div {
      margin: auto;
    }
    .promotion-type-column {
      width: 11%;
      min-width: 175px;
      &:before {
        width: 11%;
        min-width: 175px;
      }
    }
    .promotion-item-column {
      overflow: visible;
      position: relative;
      width: 16%;
      min-width: 200px;
      &:before {
        width: 16%;
        min-width: 200px;
      }

      :deep(.v-select__selection) {
        display: block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      :deep(.v-input__slot) {
        min-height: 40px !important;
      }

      .inline-price-warning {
        align-items: center;
        color: $warning-color;
        display: flex;
        font-size: 12px;
        line-height: 16px;
        margin-top: -14px;
        max-width: none;
        min-height: 16px;
        overflow: visible;
        padding: 0 4px;
        position: absolute;
        text-align: left;
        width: 760px;
        z-index: 1;

        &__icon {
          color: $warning-color;
          flex: 0 0 auto;
          margin-right: 4px;
        }

        &__text {
          display: block;
          min-width: 0;
          overflow: visible;
          text-overflow: clip;
          white-space: nowrap;
        }
      }
    }
    .promotion-name-column {
      width: 16%;
      min-width: 200px;
      &:before {
        width: 16%;
        min-width: 200px;
      }
    }
    .start-date-column,
    .end-date-column {
      width: 10%;
      min-width: 130px;
      &:before {
        width: 10%;
        min-width: 130px;
      }
    }
    .target-type-column {
      width: 16%;
      min-width: 200px;
      &:before {
        width: 16%;
        min-width: 200px;
      }
    }
    .target-item-column {
      width: 20%;
      min-width: 200px;
      &:before {
        width: 20%;
        min-width: 200px;
      }
    }
    .validation-column {
      width: 70px !important;
      text-align: center !important;
      &:before {
        width: 70px;
      }
      .validation-background {
        border-radius: 3px;
        margin: auto;
        height: 39px;
        width: 60px;
      }
      .valid {
        background-color: $green-color-bg;
      }
      .invalid {
        background-color: $red-color-bg;
      }
    }
  }
  .retailer-action-button-wrap {
    display: flex;
    justify-content: flex-start;
    margin-left: 4px;
    margin-top: -16px;
  }
}
</style>
